import { Callback } from '../interfaces/response.interface';
import { CallbackFieldMeta, CallbackFieldType, JourneyFieldValidationRules } from '../interfaces/journey.interface';
import { callbackTypeRegistry } from '../journey-engine/callback-type-registry';

interface JourneyNodeFieldRule {
  includeCallbackTypes?: string[];
  hideCallbackTypes?: string[];
  fieldTypeOverrides?: Record<string, CallbackFieldType>;
  validationByFieldName?: Record<string, JourneyFieldValidationRules>;
}

/**
 * Reusable node rules. Add node-specific presentation decisions here.
 * Key format: lowercased nodeId from Ping stage.
 */
const NODE_FIELD_RULES: Record<string, JourneyNodeFieldRule> = {
  otpcollector: {
    includeCallbackTypes: ['TextOutputCallback', 'HiddenValueCallback', 'ConfirmationCallback', 'PasswordCallback', 'NumberAttributeInputCallback', 'MetadataCallback'],
    fieldTypeOverrides: {
      PasswordCallback: 'otp',
      NumberAttributeInputCallback: 'otp',
      ConfirmationCallback: 'confirmation',
      TextOutputCallback: 'info',
    },
  },
  totpcollector: {
    includeCallbackTypes: ['TextOutputCallback', 'HiddenValueCallback', 'ConfirmationCallback', 'PasswordCallback', 'NumberAttributeInputCallback', 'NameCallback', 'MetadataCallback'],
    fieldTypeOverrides: {
      PasswordCallback: 'otp',
      NumberAttributeInputCallback: 'otp',
      NameCallback: 'otp',
      ConfirmationCallback: 'confirmation',
      TextOutputCallback: 'info',
    },
  },
  updatepwdpage: {
    includeCallbackTypes: ['HiddenValueCallback', 'TextOutputCallback', 'PasswordCallback', 'ConfirmationCallback', 'MetadataCallback'],
    fieldTypeOverrides: {
      PasswordCallback: 'password',
      ConfirmationCallback: 'confirmation',
      TextOutputCallback: 'info',
      HiddenValueCallback: 'hidden',
    },
  },
  collectname: {
    includeCallbackTypes: ['StringAttributeInputCallback', 'TextOutputCallback', 'HiddenValueCallback', 'MetadataCallback'],
    fieldTypeOverrides: {
      StringAttributeInputCallback: 'text',
    },
    validationByFieldName: {
      givenName: {
        required: true,
        trimWhitespace: true,
        maxLength: 50,
        rejectNumbersOnly: true,
        rejectSymbolsOnly: true,
        allowedPattern: "^[\\p{L}\\s'-]+$",
      },
      sn: {
        required: true,
        trimWhitespace: true,
        maxLength: 50,
        rejectNumbersOnly: true,
        rejectSymbolsOnly: true,
        allowedPattern: "^[\\p{L}\\s'-]+$",
      },
    },
  },
  confirmchange: {
    includeCallbackTypes: ['TextOutputCallback', 'ConfirmationCallback', 'HiddenValueCallback', 'MetadataCallback'],
    fieldTypeOverrides: {
      ConfirmationCallback: 'confirmation',
      TextOutputCallback: 'info',
    },
  },
  qrstep: {
    includeCallbackTypes: ['TextOutputCallback', 'HiddenValueCallback', 'ConfirmationCallback', 'MetadataCallback'],
    fieldTypeOverrides: {
      HiddenValueCallback: 'qr',
      ConfirmationCallback: 'confirmation',
      TextOutputCallback: 'info',
    },
  },
};

/**
 * Returns the UI field type for a given Ping callback type.
 */
export function mapCallbackToFieldType(callbackType: string): CallbackFieldType {
  return callbackTypeRegistry.resolve(callbackType) ?? 'text';
}

/**
 * True when a callback type is explicitly supported by a strategy.
 */
export function isSupportedCallbackType(callbackType: string): boolean {
  return callbackTypeRegistry.isSupported(callbackType);
}

/**
 * Finds a named value in a callback's output array.
 */
function getOutputValue(callback: Callback, name: string): any {
  return callback.output?.find(o => o.name === name)?.value;
}

/**
 * Extracts UI-ready field metadata from a single Ping callback.
 */
export function extractFieldMeta(callback: Callback, index: number): CallbackFieldMeta {
  const fieldName = getOutputValue(callback, 'name');
  const fieldType = mapCallbackToFieldType(callback.type);
  const label =
    getOutputValue(callback, 'prompt') ??
    fieldName ??
    getOutputValue(callback, 'message') ??
    '';

  const meta: CallbackFieldMeta = {
    fieldType,
    callbackIndex: index,
    callbackType: callback.type,
    fieldName,
    label,
    defaultValue: getOutputValue(callback, 'value') ?? callback.input?.[0]?.value ?? '',
  };

  // Choice-specific
  const choices = getOutputValue(callback, 'choices');
  if (choices) {
    meta.choices = choices;
  }

  // ConfirmationCallback uses "options" and "defaultOption"
  const confirmationOptions = getOutputValue(callback, 'options');
  if (confirmationOptions) {
    meta.choices = confirmationOptions;
  }
  const defaultOption = getOutputValue(callback, 'defaultOption');
  if (typeof defaultOption === 'number') {
    meta.defaultOption = defaultOption;
  }

  // Policy/validation hints from StringAttributeInputCallback
  const policies = getOutputValue(callback, 'policies');
  if (policies) {
    meta.policies = policies;
  }
  const failedPolicies = getOutputValue(callback, 'failedPolicies');
  if (failedPolicies) {
    meta.failedPolicies = failedPolicies;
  }

  return meta;
}

function normalizeNodeId(nodeId?: string | null): string {
  return (nodeId ?? '').trim().toLowerCase();
}

function shouldIncludeCallback(callbackType: string, rule?: JourneyNodeFieldRule): boolean {
  if (!rule) return true;

  if (rule.hideCallbackTypes?.includes(callbackType)) {
    return false;
  }

  if (rule.includeCallbackTypes?.length) {
    return rule.includeCallbackTypes.includes(callbackType);
  }

  return true;
}

export function validateTextFieldValue(value: any, rules?: JourneyFieldValidationRules): string | null {
  if (!rules) {
    return null;
  }

  const raw = String(value ?? '');
  const trimmed = raw.trim();

  if (rules.required && trimmed.length === 0) {
    return 'Required: reject empty or whitespace-only values.';
  }

  if (rules.maxLength && trimmed.length > rules.maxLength) {
    return `Limit upto ${rules.maxLength} characters.`;
  }

  if (rules.rejectNumbersOnly && /^\d+$/.test(trimmed)) {
    return 'Reject numbers-only values.';
  }

  if (rules.rejectSymbolsOnly && /^[^\p{L}\d]+$/u.test(trimmed)) {
    return 'Reject symbols-only values.';
  }

  if (rules.allowedPattern && trimmed.length > 0) {
    const regex = new RegExp(rules.allowedPattern, 'u');
    if (!regex.test(trimmed)) {
      return 'Allow letters, spaces, hyphens, apostrophes, and relevant Unicode characters.';
    }
  }

  return null;
}

/**
 * Node-aware mapper: resolves fields based on nodeId first,
 * then falls back to standard callback-type mapping.
 */
export function mapCallbacksForNode(callbacks: Callback[], nodeId?: string | null): CallbackFieldMeta[] {
  const rule = NODE_FIELD_RULES[normalizeNodeId(nodeId)];

  return callbacks
    .map((cb, index) => ({ cb, index }))
    .filter(({ cb }) => shouldIncludeCallback(cb.type, rule))
    .map(({ cb, index }) => {
      const meta = extractFieldMeta(cb, index);
      const override = rule?.fieldTypeOverrides?.[cb.type];
      if (override) {
        meta.fieldType = override;
      }

      if (meta.fieldName) {
        const validation = rule?.validationByFieldName?.[meta.fieldName];
        if (validation) {
          meta.validationRules = validation;
        }
      }

      return meta;
    });
}

/**
 * Maps all callbacks in a step to their UI field metadata.
 */
export function mapAllCallbacks(callbacks: Callback[]): CallbackFieldMeta[] {
  return mapCallbacksForNode(callbacks, null);
}
